import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineHome, HiOutlinePlus, HiOutlineTrash, HiOutlineXMark,
  HiOutlineUser, HiOutlineSparkles, HiOutlineArrowRight,
  HiOutlineExclamationTriangle, HiOutlineCalendarDays,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { roomsAPI, retreatBlocksAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import toast from "react-hot-toast";
import "./AdminRooms.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";

/* ── Add room ── */
function RoomModal({ roomTypes, onClose, onSaved }) {
  const [roomTypeId, setRoomTypeId] = useState(roomTypes[0]?.room_type_id || "");
  const [name, setName] = useState("");
  const [bedCount, setBedCount] = useState(2);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!roomTypeId) { setError("Choose a room type."); return; }
    if (!name.trim()) { setError("Room name is required."); return; }
    setSaving(true); setError("");
    try {
      await roomsAPI.create({
        room_type_id: roomTypeId, name: name.trim(),
        notes: notes.trim() || null, bed_count: parseInt(bedCount) || 0,
      });
      toast.success("Room created.");
      onSaved();
    } catch (err) { setError(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"420px"}}>
        <div className="aap__contact-head">
          <h3>New Room</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <div className="arm__field">
          <label>Room Type</label>
          <select className="arb__form-input arb__form-select" value={roomTypeId}
            onChange={e=>{setRoomTypeId(e.target.value); setError("");}}>
            {roomTypes.map(rt => (
              <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>
            ))}
          </select>
        </div>
        <div className="arm__field">
          <label>Room Name</label>
          <input className="arb__form-input" value={name} placeholder="e.g. Casa Verde 1"
            onChange={e=>{setName(e.target.value); setError("");}}/>
        </div>
        <div className="arm__field">
          <label>Number of Beds</label>
          <input type="number" min="0" max="20" className="arb__form-input" value={bedCount}
            onChange={e=>setBedCount(e.target.value)}/>
          <p className="arm__hint">Beds are created automatically. You can add or remove them later.</p>
        </div>
        <div className="arm__field">
          <label>Notes <span className="arm__optional">(optional)</span></label>
          <input className="arb__form-input" value={notes}
            onChange={e=>setNotes(e.target.value)}/>
        </div>
        {error && <p className="arm__error">{error}</p>}
        <div className="arm__modal-actions">
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Create Room"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Bed picker for assignment ── */
function BedPicker({ rooms, onPick, onClose }) {
  return (
    <div className="arm__picker" onClick={e=>e.stopPropagation()}>
      <p className="arm__picker-title">Assign to…</p>
      <div className="arm__picker-scroll">
        {rooms.map(r => (
          <div key={r.id}>
            <p className="arm__picker-room">{r.name}</p>
            {r.beds.map(b => (
              <button key={b.id} type="button" className="arm__picker-bed"
                disabled={!!b.occupant}
                onClick={()=>{ onPick(b.id); onClose(); }}>
                <span>{b.name}</span>
                {b.occupant && <span className="arm__picker-taken">{b.occupant.name}</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
      <button type="button" className="arm__picker-bed arm__picker-bed--clear"
        onClick={()=>{ onPick(null); onClose(); }}>
        Remove from bed
      </button>
    </div>
  );
}

/* ── Rooming board for one retreat ── */
function RoomingBoard({ blockId, onBack }) {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canManage = permLoading ? true : hasPermission(admin, "cohorts.manage");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(null);

  const fetchBoard = useCallback(() => {
    setLoading(true);
    roomsAPI.board(blockId)
      .then(res => setData(res?.data || null))
      .catch(err => toast.error(err.message || "Failed to load rooming board."))
      .finally(() => setLoading(false));
  }, [blockId]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  useEffect(() => {
    if (!picking) return;
    const close = () => setPicking(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [picking]);

  const assign = async (applicationId, bedId) => {
    setBusy(true);
    try {
      const res = await roomsAPI.assign(blockId, applicationId, bedId);
      toast.success(res?.message || "Updated.");
      fetchBoard();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const autoAssign = async () => {
    setBusy(true);
    try {
      const res = await roomsAPI.autoAssign(blockId);
      toast.success(res?.message || "Assigned.");
      fetchBoard();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="arb__loading"><div className="acr__spinner"/></div>;
  if (!data) return null;

  const { block, rooms, unassigned, summary } = data;

  return (
    <div>
      <button type="button" className="arm__back" onClick={onBack}>
        ← All Retreats
      </button>

      <div className="acard arm__board-head">
        <div>
          <h2 className="arm__board-title">{block.name}</h2>
          <p className="arm__board-dates">
            <HiOutlineCalendarDays/> {fmt(block.start_date)} – {fmt(block.end_date)}
          </p>
        </div>
        {canManage && unassigned.length > 0 && rooms.length > 0 && (
          <button type="button" className="a-btn a-btn--rust" disabled={busy} onClick={autoAssign}>
            <HiOutlineSparkles/> Auto-Assign {unassigned.length}
          </button>
        )}
      </div>

      <div className="aap__stats-row" style={{gridTemplateColumns:"repeat(3,1fr)"}}>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top"><p className="aap__stat-label">Beds Occupied</p></div>
          <p className="aap__stat-value">{summary.occupied}<span style={{fontSize:"0.9rem",color:"#6b6258"}}>/{summary.totalBeds}</span></p>
        </div>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top"><p className="aap__stat-label">Available</p></div>
          <p className="aap__stat-value">{summary.available}</p>
        </div>
        <div className={`acard aap__stat-card ${summary.unassignedCount>0?"arm__stat--alert":""}`}>
          <div className="aap__stat-top"><p className="aap__stat-label">Without a Bed</p></div>
          <p className="aap__stat-value">{summary.unassignedCount}</p>
        </div>
      </div>

      {rooms.length === 0 ? (
        <div className="acard arb__empty">
          <HiOutlineHome className="arb__empty-icon"/>
          <p className="arb__empty-title">No rooms for this retreat's room types</p>
          <p className="arb__empty-sub">
            Go to the Rooms tab and create rooms under the room types allocated to this retreat.
          </p>
        </div>
      ) : (
        <div className="arm__room-grid">
          {rooms.map(r => (
            <div key={r.id} className="acard arm__room-card">
              <div className="arm__room-head">
                <div>
                  <p className="arm__room-name">{r.name}</p>
                  <p className="arm__room-type">{r.room_type_name}</p>
                </div>
                <span className="arm__room-count">{r.occupiedCount}/{r.beds.length}</span>
              </div>
              <div className="arm__beds">
                {r.beds.map(b => (
                  <div key={b.id} className={`arm__bed ${b.occupant?"arm__bed--taken":""}`}>
                    <span className="arm__bed-name">{b.name}</span>
                    {b.occupant ? (
                      <span className="arm__bed-person">
                        {b.occupant.name}
                        {b.occupant.roomTypeMismatch && (
                          <HiOutlineExclamationTriangle
                            className="arm__mismatch"
                            title="This person booked a different room type"/>
                        )}
                        {canManage && (
                          <button type="button" className="arm__bed-clear" disabled={busy}
                            title="Remove from bed"
                            onClick={()=>assign(b.occupant.applicationId, null)}>
                            <HiOutlineXMark/>
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="arm__bed-empty">Empty</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {unassigned.length > 0 && (
        <div className="acard arm__unassigned">
          <p className="arm__unassigned-title">
            <HiOutlineExclamationTriangle/> Without a bed ({unassigned.length})
          </p>
          <p className="arm__unassigned-sub">
            Confirmed attendees who haven't been given a bed for this retreat.
          </p>
          {unassigned.map(u => (
            <div key={u.applicationId} className="arm__person">
              <div className="arm__person-info">
                <p className="arm__person-name"><HiOutlineUser/> {u.name}</p>
                <p className="arm__person-meta">
                  {u.category}{u.bookedRoomType ? ` · booked ${u.bookedRoomType}` : ""}
                </p>
              </div>
              {canManage && rooms.length > 0 && (
                <div className="arm__person-action">
                  <button type="button" className="a-btn a-btn--outline" disabled={busy}
                    onClick={e=>{ e.stopPropagation(); setPicking(picking===u.applicationId?null:u.applicationId); }}>
                    Assign <HiOutlineArrowRight/>
                  </button>
                  {picking === u.applicationId && (
                    <BedPicker rooms={rooms}
                      onPick={bedId => assign(u.applicationId, bedId)}
                      onClose={()=>setPicking(null)}/>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminRooms() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canEdit = permLoading ? true : hasPermission(admin, "blocks.edit");

  const [tab, setTab] = useState("rooms");
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewingBlock, setViewingBlock] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      roomsAPI.list().catch(() => null),
      retreatBlocksAPI.list({ scope: "active" }).catch(() => null),
    ]).then(([r, b]) => {
      setData(r?.data || { roomTypes: [], totalRooms: 0, totalBeds: 0 });
      setBlocks(b?.data || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addBed = async (roomId) => {
    setBusy(true);
    try { await roomsAPI.addBed(roomId); fetchAll(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const removeBed = async (roomId, bedId) => {
    setBusy(true);
    try { await roomsAPI.removeBed(roomId, bedId); fetchAll(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const removeRoom = (room) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete {room.name}?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>Its beds go too. This can't be undone.</p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async ()=>{
            toast.dismiss(t.id); setBusy(true);
            try { await roomsAPI.remove(room.id); toast.success("Deleted."); fetchAll(); }
            catch(err){ toast.error(err.message||"Failed."); }
            finally { setBusy(false); }
          }} style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete</button>
          <button onClick={()=>toast.dismiss(t.id)}
            style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"320px"} });
  };

  if (viewingBlock) {
    return (
      <AdminLayout pageTitle="Rooming Board" pageSubtitle="" permission="blocks.view">
        <RoomingBoard blockId={viewingBlock} onBack={()=>{ setViewingBlock(null); fetchAll(); }}/>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Rooms & Beds" pageSubtitle="Physical rooms, beds, and who sleeps where."
      permission="blocks.view">

      <div className="arm__tabs">
        {[{k:"rooms",label:"Rooms & Beds"},{k:"board",label:"Rooming by Retreat"}].map(t => (
          <button key={t.k} type="button"
            className={`arm__tab ${tab===t.k?"arm__tab--active":""}`}
            onClick={()=>setTab(t.k)}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="arb__loading"><div className="acr__spinner"/></div>
      ) : tab === "rooms" ? (
        <>
          <div className="aap__stats-row" style={{gridTemplateColumns:"repeat(2,1fr)"}}>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top"><p className="aap__stat-label">Rooms</p></div>
              <p className="aap__stat-value">{data?.totalRooms || 0}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top"><p className="aap__stat-label">Total Beds</p></div>
              <p className="aap__stat-value">{data?.totalBeds || 0}</p>
            </div>
          </div>

          {canEdit && (
            <div className="arm__toolbar">
              <button type="button" className="a-btn a-btn--rust"
                disabled={!data?.roomTypes?.length} onClick={()=>setAdding(true)}>
                <HiOutlinePlus/> New Room
              </button>
            </div>
          )}

          {!data?.roomTypes?.length ? (
            <div className="acard arb__empty">
              <HiOutlineHome className="arb__empty-icon"/>
              <p className="arb__empty-title">No room types yet</p>
              <p className="arb__empty-sub">
                Create room types first under Retreat Blocks → Manage Room Types, then add
                physical rooms and beds here.
              </p>
            </div>
          ) : data.roomTypes.map(rt => (
            <div key={rt.room_type_id} className="acard aq__section">
              <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0.5rem"}}>
                {rt.room_type_name}
              </p>
              {rt.rooms.length === 0 ? (
                <p className="aap__placeholder" style={{padding:"0 1.25rem 1.1rem",textAlign:"left"}}>
                  No rooms defined for this type yet.
                </p>
              ) : rt.rooms.map(room => (
                <div key={room.id} className="arm__room-row">
                  <div className="arm__room-row-main">
                    <p className="arm__room-row-name">{room.name}</p>
                    {room.notes && <p className="arm__room-row-notes">{room.notes}</p>}
                    <div className="arm__bed-chips">
                      {(room.beds||[]).map(b => (
                        <span key={b.id} className="arm__bed-chip">
                          {b.name}
                          {canEdit && (
                            <button type="button" disabled={busy}
                              onClick={()=>removeBed(room.id, b.id)} title="Remove bed">
                              <HiOutlineXMark/>
                            </button>
                          )}
                        </span>
                      ))}
                      {canEdit && (
                        <button type="button" className="arm__bed-add" disabled={busy}
                          onClick={()=>addBed(room.id)}>+ Bed</button>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}}
                      disabled={busy} onClick={()=>removeRoom(room)}><HiOutlineTrash/></button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      ) : (
        <div className="acard aq__section">
          <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0.5rem"}}>
            Pick a retreat to see its rooming board
          </p>
          {blocks.length === 0 ? (
            <p className="aap__placeholder" style={{padding:"1.5rem",textAlign:"center"}}>
              No active retreats.
            </p>
          ) : blocks.map(b => (
            <div key={b.id} className="arm__block-row" onClick={()=>setViewingBlock(b.id)}>
              <div>
                <p className="arm__block-name">{b.name}</p>
                <p className="arm__block-meta">
                  <HiOutlineCalendarDays/> {fmt(b.start_date)} – {fmt(b.end_date)}
                  {` · ${b.assigned_count} confirmed`}
                </p>
              </div>
              <HiOutlineArrowRight className="arm__block-arrow"/>
            </div>
          ))}
        </div>
      )}

      {adding && data?.roomTypes && (
        <RoomModal roomTypes={data.roomTypes}
          onClose={()=>setAdding(false)}
          onSaved={()=>{ setAdding(false); fetchAll(); }}/>
      )}
    </AdminLayout>
  );
}
